import React from "react";
import { Link } from "react-router-dom";
import { CATEGORIES, BRAND } from "../mock";
import { Mail, MapPin, Phone } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import Logo from "./Logo";
import PaymentIcons from "./PaymentIcons";
import { apiSubscribe } from "../lib/api";
import { toast } from "sonner";

const Footer = () => {
  const onSubscribe = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const email = new FormData(form).get("email");
    if (!email) return;
    try {
      await apiSubscribe(email, "footer");
      toast.success(`Subscribed. Use code ${BRAND.coupon} at checkout.`);
      form.reset();
    } catch {
      toast.error("Could not subscribe — please try again.");
    }
  };

  return (
    <footer className="bg-[#0a0a0a] text-gray-300 mt-16">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10">
        {/* Brand block */}
        <div className="lg:col-span-5">
          <Logo size={110} inverted />
          <p className="text-[14px] text-gray-400 max-w-md leading-relaxed font-light mt-6">
            Precision peptide research. {BRAND.name} supplies American-made,
            laboratory-verified research compounds trusted by clinicians, researchers,
            and performance professionals nationwide.
          </p>
          <form onSubmit={onSubscribe} className="mt-8 max-w-md">
            <div className="text-[10px] font-semibold tracking-[0.25em] uppercase text-red-400 mb-2">Newsletter</div>
            <div className="flex gap-2">
              <Input
                name="email"
                type="email"
                required
                placeholder="your@email.com"
                className="bg-white/5 border-white/15 rounded-none text-white placeholder:text-gray-500 h-11"
              />
              <Button type="submit" className="btn-primary rounded-none h-11 px-6 text-[12px] font-semibold uppercase tracking-wider">
                Subscribe
              </Button>
            </div>
            <p className="text-[11px] text-gray-500 mt-2">Get 10% off with code {BRAND.coupon}</p>
          </form>
        </div>

        <div className="lg:col-span-2 lg:col-start-7">
          <h4 className="text-[10px] font-semibold tracking-[0.25em] uppercase text-red-400 mb-4">Shop</h4>
          <ul className="space-y-2.5 text-[13px]">
            <li><Link className="hover:text-white transition-colors" to="/shop">All Products</Link></li>
            {CATEGORIES.map((c) => (
              <li key={c.slug}><Link className="hover:text-white transition-colors" to={`/shop/${c.slug}`}>{c.name}</Link></li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-2">
          <h4 className="text-[10px] font-semibold tracking-[0.25em] uppercase text-red-400 mb-4">Company</h4>
          <ul className="space-y-2.5 text-[13px]">
            <li><Link className="hover:text-white transition-colors" to="/about">About</Link></li>
            <li><Link className="hover:text-white transition-colors" to="/blog">Research Journal</Link></li>
            <li><Link className="hover:text-white transition-colors" to="/contact">Contact</Link></li>
            <li><Link className="hover:text-white transition-colors" to="/faq">FAQ</Link></li>
            <li><Link className="hover:text-white transition-colors" to="/disclaimer">Research Disclaimer</Link></li>
          </ul>
        </div>

        <div className="lg:col-span-3">
          <h4 className="text-[10px] font-semibold tracking-[0.25em] uppercase text-red-400 mb-4">Contact</h4>
          <ul className="space-y-3 text-[13px]">
            <li className="flex items-start gap-2"><Phone className="h-3.5 w-3.5 mt-0.5 text-gray-400" /> {BRAND.phone}</li>
            <li className="flex items-start gap-2"><Mail className="h-3.5 w-3.5 mt-0.5 text-gray-400" /> {BRAND.email}</li>
            <li className="flex items-start gap-2"><MapPin className="h-3.5 w-3.5 mt-0.5 text-gray-400" /> Made & shipped from the USA</li>
          </ul>
          <div className="mt-6">
            <div className="text-[10px] font-semibold tracking-[0.25em] uppercase text-red-400 mb-3">We Accept</div>
            <PaymentIcons dark />
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-10 py-5 text-center">
          <p className="text-[10px] font-semibold tracking-[0.4em] uppercase text-red-400">{BRAND.mantra}</p>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-10 py-8 text-[11px] text-gray-500 leading-relaxed font-light space-y-3 max-w-4xl">
          <p>
            All products on this site are for research and development use only. Products are not for human consumption of any kind. The statements made on this website have not been evaluated by the U.S. Food and Drug Administration. The statements and the products of this company are not intended to diagnose, treat, cure, or prevent any disease.
          </p>
          <p>
            <strong className="text-gray-400">{BRAND.name} is a chemical supplier.</strong> {BRAND.name} is not a compounding pharmacy or chemical compounding facility as defined under 503A of the Federal Food, Drug, and Cosmetic Act. {BRAND.name} is not an outsourcing facility as defined under 503B of the Federal Food, Drug, and Cosmetic Act.
          </p>
          <p>
            All products are sold for research, laboratory, or analytical purposes only, and are not for human consumption.
          </p>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-[11px] text-gray-500">
          <p>© {new Date().getFullYear()} {BRAND.name}. All rights reserved.</p>
          <p className="text-center max-w-2xl">
            <strong className="text-gray-400">FOR LABORATORY RESEARCH USE ONLY ·</strong> Not for human or animal consumption. Must be 18+ to purchase.
          </p>
          <div className="flex gap-4 flex-wrap justify-center">
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <Link to="/refund-policy" className="hover:text-white">Refunds</Link>
            <Link to="/disclaimer" className="hover:text-white">Disclaimer</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
